import React from 'react';

import { formatMoney } from '@betterment/core';
import { useMatchMedia } from '@betterment/js-runtime';
import { Box, Heading, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { token } from '@betterment/samba-styled-system/tokens';
import { AxisBottom, AxisLeft, TickRendererProps } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { AreaClosed, Bar, Line, LinePath } from '@visx/shape';
import { Text as VisXText } from '@visx/text';
import { format } from 'd3-format';
import { timeFormat } from 'd3-time-format';

import { BalanceProjectionItem } from '../../../../graphql/schema';
import { useXYChartTooltipInPortal } from '../components/XYChartTooltip';
import { TimeDomainScale, useChartHighlight } from '../hooks/useChartHighlight';
import { useChartScale } from '../hooks/useChartScale';
import { useTickLabelProps } from '../hooks/useTickLabelProps';

type Props = {
  data: BalanceProjectionItem[];
  width: number;
  height: number;
  target?: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  showLeftAxis?: boolean;
};

const FLAG_WIDTH = 22;
const DEFAULT_MARGIN = { top: 0, right: 0, bottom: 0, left: 0 };

const xAccessor = (d: BalanceProjectionItem) => d.date;
const y0Accessor = (d: BalanceProjectionItem) => d.pessimistic.cents;
const y1Accessor = (d: BalanceProjectionItem) => d.optimistic.cents;
const yExpectedAccessor = (d: BalanceProjectionItem) => d.expected.cents;
const yDepositsAccessor = (d: BalanceProjectionItem) => d.netDeposits.cents;

const formatDate = timeFormat('%b, %Y');
const formatCents = (v: number) => (v === 0 ? format('$.0s')(v) : format('$.2s')(v / 100).replace('G', 'B'));
const formatCentsTooltip = (v: number) => (v === 0 ? format('$.0s')(v) : format('$.3s')(v / 100).replace('G', 'B'));

const CustomTick = ({ formattedValue, ...rest }: TickRendererProps) => (
  <VisXText className={css({ fontSize: '10px', fill: 'text.weak' })} {...rest}>
    {formattedValue}
  </VisXText>
);

const Flag = ({ x, y }: { x: number; y: number }) => {
  const flagHeight = 21;
  return (
    <g transform={`translate(${x}, ${y - flagHeight / 2})`} data-testid="target-flag">
      <svg xmlns="http://www.w3.org/2000/svg" width={FLAG_WIDTH} height={flagHeight} fill="none" viewBox="0 0 22 21">
        <path
          fill={token('colors.icon')}
          d="M9 6h2V4H9v2Zm4 0V4h2v2h-2Zm-4 8v-2h2v2H9Zm8-4V8h2v2h-2Zm0 4v-2h2v2h-2Zm-4 0v-2h2v2h-2Zm4-8V4h2v2h-2Zm-6 2V6h2v2h-2ZM5 20V4h2v2h2v2H7v2h2v2H7v8H5Zm10-8v-2h2v2h-2Zm-4 0v-2h2v2h-2Zm-2-2V8h2v2H9Zm4 0V8h2v2h-2Zm2-2V6h2v2h-2Z"
        />
      </svg>
    </g>
  );
};

export const ProjectionChart: React.FC<Props> = ({
  data,
  width,
  height,
  target,
  margin = DEFAULT_MARGIN,
  showLeftAxis = true,
}) => {
  const { xScale, xMax, yScale, yMax } = useChartScale({ data, target, width, height, margin });

  const chartHighlight = useChartHighlight<BalanceProjectionItem, TimeDomainScale>({
    dateScale: xScale,
    data,
    xAccessor,
    marginLeft: margin.left,
  });
  const { highlightData, handleHighlightUpdate, handleHighlightCancel } = chartHighlight;

  const { XYChartTooltipInPortal, ClampedXYChartTooltipInPortal, containerRef } = useXYChartTooltipInPortal();

  const isAtMediumScreen = useMatchMedia('atLeastMedium');
  const tooltipWidth = isAtMediumScreen ? 350 : 300;
  const tickLabelProps = useTickLabelProps({ highlightData });

  return (
    <Box css={{ position: 'relative' }}>
      <svg width={width} height={height} ref={containerRef}>
        <rect x={0} y={0} width={width} height={height} fill={token('colors.background.surface')} />
        <Group left={margin.left} top={margin.top}>
          {showLeftAxis && (
            <>
              <AxisLeft
                scale={yScale}
                numTicks={4}
                tickFormat={v => formatCents(v.valueOf())}
                hideAxisLine={true}
                hideTicks={true}
                tickComponent={CustomTick}
              />
              <GridRows
                scale={yScale}
                width={xMax}
                height={yMax}
                strokeDasharray="4,4"
                stroke={token('colors.border.weak')}
                pointerEvents="none"
                numTicks={5}
              />
            </>
          )}
          {/* X Axis */}
          <AxisBottom
            scale={xScale}
            top={yMax}
            tickValues={[xAccessor(data[0]), xAccessor(data[data.length - 1])]}
            tickFormat={v => formatDate(v as Date)}
            hideTicks={true}
            rangePadding={0}
            labelOffset={0}
            axisLineClassName={css({ stroke: 'border' })}
            tickLabelProps={tickLabelProps}
          />

          {/* Area Between Pessimistic and Optimistic */}
          <AreaClosed
            className={css({ fill: 'dataviz.blue.overlay' })}
            data={data}
            yScale={yScale}
            x={d => xScale(xAccessor(d))}
            y0={d => yScale(y0Accessor(d))}
            y1={d => yScale(y1Accessor(d))}
          />
          <Bar
            x={0}
            y={0}
            width={xMax}
            height={yMax}
            fill="transparent"
            onTouchStart={handleHighlightUpdate}
            onTouchMove={handleHighlightUpdate}
            onMouseMove={handleHighlightUpdate}
            onMouseLeave={handleHighlightCancel}
          />
          {/* Expected balance */}
          <LinePath
            className={css({ stroke: 'dataviz.blue.2', strokeWidth: 'thick', pointerEvents: 'none' })}
            data={data}
            x={d => xScale(xAccessor(d))}
            y={d => yScale(yExpectedAccessor(d))}
          />
          {/* Net deposits */}
          <LinePath
            className={css({ stroke: 'border.strong', strokeWidth: 'thick', pointerEvents: 'none' })}
            data={data}
            x={d => xScale(xAccessor(d))}
            y={d => yScale(yDepositsAccessor(d))}
          />
          {/* Target axis */}
          {target && (
            <g>
              <LinePath
                className={css({
                  stroke: 'border.strongest',
                  strokeWidth: '2px',
                  strokeDasharray: '4, 4',
                  pointerEvents: 'none',
                })}
                data={data}
                x={d => xScale(xAccessor(d))}
                y={_ => yScale(target)}
                data-testid="target-line-path"
              />
              <Flag x={xMax} y={yScale(target)} />
            </g>
          )}
          {highlightData && (
            <g>
              <Line
                from={{ x: highlightData.highlightLeft, y: -10 }}
                to={{ x: highlightData.highlightLeft, y: height - margin.bottom - margin.top }}
                stroke={token('colors.border.strongest')}
                strokeWidth={1}
                pointerEvents="none"
              />
              <circle
                cx={highlightData.highlightLeft}
                cy={height - margin.bottom - margin.top}
                r={1.5}
                fill={token('colors.border.strongest')}
                stroke={token('colors.border.strongest')}
                strokeWidth={2}
                pointerEvents="none"
              />
            </g>
          )}
        </Group>
      </svg>
      {highlightData && (
        <Box>
          <ClampedXYChartTooltipInPortal
            tooltipLeft={highlightData.highlightLeft}
            chartWidth={width}
            margin={margin}
            tooltipWidth={tooltipWidth}
            data-testid="chart-tooltip"
          >
            <Box css={{ display: 'flex', flexDir: 'column', gap: 'space.50' }}>
              <Heading variant="title100">{formatDate(highlightData.highlight.date)}</Heading>
              <Box
                css={{
                  display: 'grid',
                  gridTemplateColumns: 'auto auto 1fr',
                  gridTemplateRows: 'repeat(3, auto)',
                  alignItems: 'center',
                  columnGap: 'space.100',
                  rowGap: 'space.50',
                }}
              >
                <Box
                  css={{
                    height: '3px',
                    width: '12px',
                    backgroundColor: 'dataviz.blue.2',
                    borderRadius: 'round',
                    marginBottom: '2px',
                  }}
                />
                <Text variant="text50">Average outcome</Text>
                <Text variant="text50" css={{ justifySelf: 'end', textAlign: 'end' }}>
                  {formatMoney(highlightData.highlight.expected, 'defaultDollar')}
                </Text>
                <Box
                  css={{
                    height: 'size.100',
                    width: 'size.100',
                    backgroundColor: 'dataviz.blue.overlay',
                    borderRadius: 'round',
                    marginBottom: '2px',
                  }}
                />

                <Text variant="text50" css={{ gridColumn: '2/3' }}>
                  Outcome range
                </Text>
                <Text variant="text50" css={{ gridColumn: '3/4', justifySelf: 'end', textAlign: 'end' }}>
                  {formatCentsTooltip(highlightData.highlight.pessimistic.cents)}-
                  {formatCentsTooltip(highlightData.highlight.optimistic.cents)}
                </Text>

                <Box
                  css={{
                    display: 'grid',
                    gridTemplateColumns: 'subgrid',
                    gridColumn: '1/4',
                    rowGap: 'space.0',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    css={{
                      height: '2px',
                      width: '10px',
                      backgroundColor: 'border.strong',
                      borderRadius: 'round',
                      marginBottom: '2px',
                      gridColumn: '1/2',
                    }}
                  />
                  <Text variant="text50" css={{ gridColumn: '2/3' }}>
                    Net deposits
                  </Text>
                  <Text variant="text50" css={{ gridColumn: '3/4', justifySelf: 'end', textAlign: 'end' }}>
                    {formatMoney(highlightData.highlight.netDeposits, 'defaultDollar')}
                  </Text>
                  <Text variant="text25" color="text.weak" css={{ gridColumn: '2/4' }}>
                    Net deposits includes recurring deposits, transfers, and withdrawals for this account.
                  </Text>
                </Box>
              </Box>
            </Box>
          </ClampedXYChartTooltipInPortal>
          <XYChartTooltipInPortal tooltipLeft={highlightData.highlightLeft} chartHeight={height} margin={margin}>
            {formatDate(highlightData.highlight.date)}
          </XYChartTooltipInPortal>
        </Box>
      )}
    </Box>
  );
};
